import {
  FormatQuoteOutlined,
  FactCheckOutlined,
  CategoryOutlined,
} from "@mui/icons-material";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import AutoOpenToggle from "src/reusable/Sidebar/AutoOpenToggle";
import SourcesDownloadButton from "src/views/assistant/components/sources/SourcesDownloadButton";
import SourcesViewToggle from "src/views/assistant/components/sources/SourcesViewToggle";
import { SidebarConfig } from "src/reusable/Sidebar/types";
import ClaimsTab from "../components/sidebar/ClaimsTab";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import { Dispatch, SetStateAction } from "react";

interface VnvAssistSidebarContext {
  dialogueId: number | undefined;
  responseSources: any[];
  artifacts: Record<string, any>;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
}

export const useVnvAssistSidebar = (
  _context: VnvAssistSidebarContext,
): SidebarConfig<VnvAssistSidebarContext> => {
  const { preferences } = useUIStore();
  const claims = useVnvAssistStore((s) => s.claims);

  return {
    tabs: [
      {
        id: "claims",
        label: "Claims",
        icon: FactCheckOutlined,
        renderContent: () => <ClaimsTab claims={claims} />,
        renderHeaderActions: () => null,
        getBadgeContent: () => claims.length,
      },
      {
        id: "sources",
        label: "Sources",
        icon: FormatQuoteOutlined,
        renderContent: ({ context: ctx }) => {
          if (!ctx) return null;
          return (
            <SourcesTab
              sources={ctx.responseSources}
              viewMode={ctx.viewMode}
              setViewMode={ctx.setViewMode}
              detailSourceViewSdrId={null}
              detailSourceViewSourceId={null}
              setDetailSourceViewSdrId={() => {}}
              setDetailSourceViewSourceId={() => {}}
              // Three-tier provenance funnel (#20423 Story 4): without the
              // dialogue id SourcesTab silently falls back to the legacy list.
              dialogueId={ctx.dialogueId ?? null}
            />
          );
        },
        renderHeaderActions: ({ context: ctx }) => {
          if (!ctx) return null;
          return (
            <>
              <SourcesDownloadButton responseSources={ctx.responseSources} />
              <SourcesViewToggle
                alignment={ctx.viewMode}
                handleAlignment={(_, v) => {
                  if (v) ctx.setViewMode(v as "dataChunk" | "cardList");
                }}
              />
              <AutoOpenToggle />
            </>
          );
        },
        getBadgeContent: (ctx) => ctx?.responseSources.length ?? 0,
      },
      {
        id: "artifacts",
        label: "Artifacts",
        icon: CategoryOutlined,
        renderContent: ({ context: ctx }) => {
          if (!ctx) return null;
          return <ArtifactsTab artifacts={ctx.artifacts} />;
        },
        renderHeaderActions: () => <AutoOpenToggle />,
        getBadgeContent: (ctx) =>
          ctx?.artifacts ? Object.keys(ctx.artifacts).length : 0,
      },
    ],
    defaultTab: "claims",
    responsive: {
      enableMobile: true,
      enableDesktopNav: true,
      enableResize: true,
      minWidth: 300,
      maxWidth: 800,
      defaultWidth: 400,
    },
    autoOpen: {
      enabled: () => preferences?.autoOpenSidebar ?? true,
      getContentCounts: (ctx) => ({
        sources: ctx?.responseSources.length ?? 0,
        artifacts: ctx?.artifacts ? Object.keys(ctx.artifacts).length : 0,
      }),
      autoSelectTab: "sources",
      triggerKey: "sources",
    },
  };
};
